'use client'

import React, { useEffect, useMemo } from 'react'
import { useUserContext } from '@/lib/contexts/UserContext'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { Button, buttonVariants } from '@/components/ui/button'
import Link from 'next/link'
import styles from './asset-popup.module.scss'
import client from '@/lib/api/client'
import Image from 'next/image'
import { cn, formatBytes } from '@/lib/utils'
import Asset360Viewer from '@/components/asset-360-viewer/asset-360-viewer'
import { TbView360Number } from 'react-icons/tb'
import { ReactPhotoSphereViewer } from 'react-photo-sphere-viewer'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'
import { useHotkeys } from 'react-hotkeys-hook'
import { MusicBar } from '@/components/ui/music-bar'
import { DownloadOption, FormatOption, ResolutionOption } from '@/lib/hooks/useAssetDownload'
import useAssetDetail from '@/lib/hooks/useAssetDetail'
import { Asset, AssetType } from '@/lib/assets/types'

// TODO: Convert TailwindCSS to SCSS
export type AssetPopupProps = {
  type: AssetType
  assetId: string | number | null
  mainCategoryId: string
  categoryId?: string
  subCategoryId?: string
  contentId?: string
  hasPrev?: boolean
  hasNext?: boolean
  onOpenChange?: (open: boolean) => void
  onNext?: () => void
  onPrev?: () => void
  disableNavigation?: boolean
}

const AssetPopup = ({
  type,
  assetId,
  mainCategoryId,
  categoryId,
  subCategoryId,
  contentId,
  hasPrev,
  hasNext,
  onOpenChange = () => {},
  onNext = () => {},
  onPrev = () => {},
  disableNavigation,
}: AssetPopupProps) => {
  const [resolutionCardOpen, setResolutionCardOpen] = React.useState<boolean | undefined>(undefined)
  const [formatCardOpen, setFormatCardOpen] = React.useState<boolean | undefined>(undefined)
  const [isMetaKeyPressed, setIsMetaKeyPressed] = React.useState<boolean>(false)
  const { user, userToken } = useUserContext()

  const { data: asset, isLoading } = useQuery<Asset>({
    queryKey: ['asset', assetId],
    queryFn: () =>
      client
        .get<Asset>(`/assets/${assetId}`, {
          params: {
            type,
            main_category: mainCategoryId,
            category: categoryId,
            sub_category: subCategoryId,
            content: contentId,
          },
        })
        .then(({ data }) => data),
    enabled: !!assetId,
  })

  const {
    selectedFormat,
    setSelectedFormat,
    selectedResolution,
    setSelectedResolution,
    formats,
    resolutions,
    videoPreview,
    imagePreview,
    asset360Preview,
    panoramaPreview,
    audioPreview,
    assetDetailUrl,
    downloadUrl,
    dispatchAssetDownloadEvent,
    downloadOptions,
  } = useAssetDetail({ asset, userToken, settings: user?.settings })

  // Debug resolutions and formats
  useEffect(() => {
    if (selectedResolution) {
      console.debug(`Available formats for ${selectedResolution.label} resolution:`, formats)
    }
  }, [formats, selectedResolution])

  // Debug selected resolution and format
  useEffect(() => {
    console.debug('Selected resolution:', selectedResolution)
    console.debug('Selected format:', selectedFormat)
  }, [selectedResolution, selectedFormat])

  // Use left and right arrow keys to navigate between assets
  useHotkeys('ArrowLeft', () => hasPrev && onPrev())
  useHotkeys('ArrowRight', () => hasNext && onNext())
  useHotkeys('meta', () => setIsMetaKeyPressed(true), { keydown: true, keyup: false })
  useHotkeys('meta', () => setIsMetaKeyPressed(false), { keydown: false, keyup: true })

  const dispatchPortalDownload = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey && asset) {
      event.preventDefault()
      const location = 'location' in asset ? asset.location : asset.file_name
      fetch('https://member.productioncrate.com/download-queue.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          location,
        }),
      })
        .then((data) => {
          console.debug('Added to download queue:', data)
          alert('Downloading through portal')
        })
        .catch((error) => {
          console.error(error)
          alert("Couldn't download through portal...")
        })
    }
  }

  // Based on selectFormat and selectResolution, get the selected option
  const selectedOption: DownloadOption | null = useMemo(() => {
    if (!downloadOptions || !selectedFormat || !selectedResolution) {
      return null
    }
    return (
      downloadOptions.find(
        (option) => option.format === selectedFormat.label && option.resolution === selectedResolution.label
      ) ?? null
    )
  }, [downloadOptions, selectedFormat, selectedResolution])

  return (
    <Dialog
      open={!!assetId}
      onOpenChange={(value) => {
        if (!value) {
          setSelectedResolution(null)
          setSelectedFormat(null)
        }

        onOpenChange(value)
      }}
    >
      <DialogContent className={cn(styles.popup, 'tw-relative tw-@container')}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <>
              {isLoading && <Skeleton className={styles.titleSkeleton} />}
              {!isLoading && !!asset && 'title' in asset && (
                <>
                  <Link className="hover:tw-underline" href={assetDetailUrl ?? '#'}>
                    {asset.title}
                  </Link>
                </>
              )}
            </>
          </DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-flex-col tw-space-y-4">
          {isLoading && (
            <>
              <Skeleton className={styles.preview} />
              <Skeleton className="tw-h-10 tw-w-full" />
            </>
          )}
          {!isLoading && !!asset && (
            <>
              {/*Use the key prop to force re-render when the value changes*/}
              <div
                className="tw-relative tw-flex tw-min-h-[360px] tw-w-full tw-flex-col tw-justify-center tw-bg-[var(--Primary)] tw-duration-300 tw-animate-in tw-fade-in"
                key={`asset-popup-preview-${assetId}`}
              >
                {/* Asset has a video preview */}
                {!!videoPreview && (
                  <video
                    data-poster={imagePreview ?? undefined}
                    preload="auto"
                    autoPlay={true}
                    muted
                    loop
                    playsInline={true}
                    poster=""
                    className={cn(styles.preview, 'tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full')}
                  >
                    <source src={videoPreview} type="video/mp4"></source>
                  </video>
                )}

                {/* Asset has an image preview */}
                {!videoPreview && !!imagePreview && (
                  <Image
                    src={imagePreview}
                    alt={'title' in asset ? asset.title : ''}
                    width={640}
                    height={0}
                    style={{
                      width: '100%',
                      maxWidth: '640px',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    className={cn(styles.imagePreview, 'tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full')}
                  />
                )}

                {/*Asset has a 360 preview*/}
                {!!asset360Preview && (
                  <>
                    <Asset360Viewer
                      className="tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full"
                      friction={30}
                      sprite={asset360Preview}
                      frames={16} // Number of frames in the sprite sheet
                      frameWidth={640} // Width of each frame
                      frameHeight={360} // Height of each frame
                    />
                    <TbView360Number
                      title="360º preview"
                      className="tw-pointer-events-none tw-absolute tw-bottom-2 tw-left-2 tw-h-6 tw-w-6 tw-text-[var(--Primary-100)] tw-opacity-50"
                    />
                  </>
                )}

                {/*Panorama preview*/}
                {!!panoramaPreview && (
                  <>
                    <ReactPhotoSphereViewer
                      src={panoramaPreview}
                      height={'100%'}
                      width={'100%'}
                      navbar={false}
                      defaultZoomLvl={0}
                    ></ReactPhotoSphereViewer>
                    <TbView360Number
                      title="360º preview"
                      className="tw-pointer-events-none tw-absolute tw-bottom-2 tw-left-2 tw-h-6 tw-w-6 tw-text-[var(--Primary-100)] tw-opacity-50"
                    />
                  </>
                )}

                {/*Audio preview*/}
                {audioPreview && <MusicBar asset={asset} hideTitle hideTools />}
              </div>

              {/*Download options*/}
              <div
                key={`asset-popup-download-options-${assetId}`}
                className="tw-flex tw-w-full tw-flex-col tw-items-start tw-justify-start tw-space-y-2 @xl:tw-flex-row @xl:tw-items-center @xl:tw-justify-between @xl:tw-space-x-2 @xl:tw-space-y-0"
              >
                {/* Dimensions */}
                {'dimensions' in asset && (
                  <div
                    className={`tw-flex tw-items-end tw-space-x-1 @xl:tw-flex-1 @xl:tw-flex-col @xl:tw-items-start @xl:tw-space-x-0`}
                  >
                    <div className="subheading_4">{asset.dimensions}</div>

                    {'seconds' in asset && 'fps' in asset && !!asset.seconds && asset.fps && (
                      <div className="body_4">
                        {(Math.round(parseFloat(asset.seconds) * 100) / 100).toFixed(2)} seconds, {asset.fps} FPS
                      </div>
                    )}
                  </div>
                )}

                {/* Download options */}
                <div className="tw-flex tw-items-center tw-space-x-2">
                  {/*Resolution hover card*/}
                  {resolutions.length > 0 && (
                    <HoverCard
                      openDelay={0}
                      closeDelay={100}
                      open={resolutionCardOpen}
                      onOpenChange={(open) => {
                        setResolutionCardOpen(open ?? undefined)
                      }}
                    >
                      <HoverCardTrigger asChild>
                        <div className="tw-flex tw-cursor-pointer tw-items-center tw-p-2">
                          <ChevronDownIcon className="tw-ml-auto tw-mr-1 tw-h-4 tw-w-4" />
                          {selectedResolution?.label}
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="top"
                        sideOffset={0}
                        align="start"
                        className="!tw-rounded tw-border-0 !tw-bg-[var(--Primary)] !tw-p-2"
                      >
                        {React.Children.toArray(
                          resolutions.map((resolution: ResolutionOption) => (
                            <button
                              type="button"
                              className={cn(
                                'tw-relative tw-flex tw-w-full tw-cursor-default tw-p-2 tw-outline-none tw-transition-all tw-duration-150 tw-ease-in-out hover:tw-font-bold',
                                {
                                  'tw-font-bold': selectedResolution?.value === resolution.value,
                                }
                              )}
                              key={`resolution-item-${resolution.value}`}
                              onClick={() => {
                                setSelectedResolution(resolution)
                                setResolutionCardOpen(false)
                              }}
                            >
                              {resolution.label}
                            </button>
                          ))
                        )}
                      </HoverCardContent>
                    </HoverCard>
                  )}

                  {/*Resolution dropdown*/}
                  {/*{resolutions.length > 0 && (*/}
                  {/*  <DropdownMenu className={`tw-ml-1`}>*/}
                  {/*    <DropdownMenuTrigger>{selectedResolution?.label}</DropdownMenuTrigger>*/}
                  {/*    <DropdownMenuContent>*/}
                  {/*      {resolutions.map((resolution: ResolutionOption) => (*/}
                  {/*        <DropdownMenuItem*/}
                  {/*          key={`resolution-item-${resolution.value}`}*/}
                  {/*          onClick={() => setSelectedResolution(resolution)}*/}
                  {/*        >*/}
                  {/*          {resolution.label}*/}
                  {/*        </DropdownMenuItem>*/}
                  {/*      ))}*/}
                  {/*    </DropdownMenuContent>*/}
                  {/*  </DropdownMenu>*/}
                  {/*)}*/}

                  {/*Formats hover card*/}
                  {formats.length > 0 && (
                    <HoverCard
                      openDelay={0}
                      closeDelay={100}
                      open={formatCardOpen}
                      onOpenChange={(open) => {
                        setFormatCardOpen(open ?? undefined)
                      }}
                    >
                      <HoverCardTrigger asChild>
                        <div className="tw-flex tw-cursor-pointer tw-items-center tw-p-2">
                          <ChevronDownIcon className="tw-ml-auto tw-mr-1 tw-h-4 tw-w-4" />
                          {selectedFormat?.label}
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="top"
                        sideOffset={0}
                        align="start"
                        className="!tw-rounded tw-border-0 !tw-bg-[var(--Primary)] !tw-p-2"
                      >
                        {formats.map((format: FormatOption) => (
                          <button
                            key={`format-item-${format.value}`}
                            type="button"
                            className={cn(
                              'tw-relative tw-flex tw-w-full tw-cursor-default tw-p-2 tw-outline-none tw-transition-all tw-duration-150 tw-ease-in-out hover:tw-font-bold',
                              {
                                'tw-font-bold': selectedFormat?.value === format.value,
                              }
                            )}
                            onClick={() => {
                              setSelectedFormat(format)
                              setFormatCardOpen(false)
                            }}
                          >
                            {format.label}
                          </button>
                        ))}
                      </HoverCardContent>
                    </HoverCard>
                  )}

                  {!!selectedOption && selectedOption.size && (
                    <div className="tw-p-2">{formatBytes(selectedOption.size)}</div>
                  )}

                  {/*/!*Formats dropdown*!/*/}
                  {/*{formats.length > 0 && (*/}
                  {/*  <DropdownMenu className={`tw-ml-1`}>*/}
                  {/*    <DropdownMenuTrigger>{selectedFormat?.label}</DropdownMenuTrigger>*/}
                  {/*    <DropdownMenuContent>*/}
                  {/*      {formats.map((format: FormatOption) => (*/}
                  {/*        <DropdownMenuItem*/}
                  {/*          key={`resolution-item-${format.value}`}*/}
                  {/*          onClick={() => setSelectedFormat(format)}*/}
                  {/*        >*/}
                  {/*          {format.label}*/}
                  {/*        </DropdownMenuItem>*/}
                  {/*      ))}*/}
                  {/*    </DropdownMenuContent>*/}
                  {/*  </DropdownMenu>*/}
                  {/*)}*/}
                </div>

                {/*Download button*/}
                <a
                  href={downloadUrl ?? '#'}
                  rel="noopener noreferrer"
                  className={cn('tw-ml-1', buttonVariants(), '!tw-mt-0 tw-w-full @xl:tw-w-auto')}
                  onClick={(event) => {
                    dispatchPortalDownload(event)
                    dispatchAssetDownloadEvent()
                  }}
                >
                  {isMetaKeyPressed ? 'Portal' : 'Download'}
                </a>
              </div>

              {/*{false && (*/}
              {/*  <div className={styles.footer}>*/}
              {/*    {keywords.length > 0 && <div className={`body_5`}>Tags</div>}*/}
              {/*    {keywords*/}
              {/*      .filter((e) => !!e)*/}
              {/*      .map((tag: any, index: any) => (*/}
              {/*        <span key={index} className={`${styles.chip} body_5 tw-mr-1`}>*/}
              {/*          {tag}*/}
              {/*        </span>*/}
              {/*      ))}*/}
              {/*  </div>*/}
              {/*)}*/}

              {/*TODO: Related assets*/}
            </>
          )}
        </div>
        {!!hasPrev && (
          <Button
            className="tw-absolute tw--left-12 tw-top-1/2 !tw-hidden tw-translate-y--1/2 md:!tw-inline-flex"
            variant={'ghost'}
            size={'icon'}
            shape={'rounded'}
            onClick={onPrev}
            title={'Previous asset'}
            disabled={disableNavigation}
          >
            <ArrowLeftIcon className="tw-h-6 tw-w-6" />
          </Button>
        )}
        {!!hasNext && (
          <Button
            className="tw-absolute tw--right-12 tw-top-1/2 !tw-hidden tw-translate-y--1/2 md:!tw-inline-flex"
            variant={'ghost'}
            size={'icon'}
            shape={'rounded'}
            onClick={onNext}
            title={'Next asset'}
            disabled={disableNavigation}
          >
            <ArrowRightIcon className="tw-h-6 tw-w-6" />
          </Button>
        )}
      </DialogContent>
    </Dialog>
  )
}

export { AssetPopup }
